<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import InputRadioGroup from './components/InputRadioGroup.vue';
import SingleSelectDropdown from './components/SingleSelectDropdown.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    SettingsSection,
    InputRadioGroup,
    SingleSelectDropdown,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isSMTPEnabled: false,
      selectedDeliveryMethod: 'smtp',
      address: '',
      port: '',
      login: '',
      password: '',
      domain: '',
      sendgridApiKey: '',
      sendgridFromEmail: '',
      sendgridFromName: '',
      ssl: false,
      starttls: true,
      openSSLVerifyMode: 'none',
      authMechanism: 'login',
      encryptionProtocols: [
        { id: 'ssl', title: 'SSL/TLS', checked: false },
        { id: 'starttls', title: 'STARTTLS', checked: true },
      ],
      openSSLVerifyModes: [
        { key: 1, value: 'none' },
        { key: 2, value: 'peer' },
        { key: 3, value: 'client_once' },
        { key: 4, value: 'fail_if_no_peer_cert' },
      ],
      authMechanisms: [
        { key: 1, value: 'plain' },
        { key: 2, value: 'login' },
        { key: 3, value: 'cram-md5' },
        { key: 4, value: 'xoauth' },
        { key: 5, value: 'xoauth2' },
        { key: 6, value: 'ntlm' },
        { key: 7, value: 'gssapi' },
      ],
    };
  },
  validations() {
    return {
      address: {
        required: requiredIf(() => this.isSmtpDeliveryEnabled),
      },
      port: {
        required: requiredIf(() => this.isSmtpDeliveryEnabled),
        minLength: value => {
          if (!this.isSmtpDeliveryEnabled) return true;
          return String(value ?? '').length >= 2;
        },
      },
      login: {
        required: requiredIf(() => this.isSmtpDeliveryEnabled),
      },
      password: {
        required: requiredIf(() => this.isSmtpDeliveryEnabled),
      },
      domain: {
        required: requiredIf(() => this.isSmtpDeliveryEnabled),
      },
      sendgridApiKey: {
        required: requiredIf(
          () => this.selectedDeliveryMethod === 'sendgrid_api'
        ),
      },
      sendgridFromEmail: {
        required: requiredIf(
          () => this.selectedDeliveryMethod === 'sendgrid_api'
        ),
      },
      sendgridFromName: {
        required: requiredIf(
          () => this.selectedDeliveryMethod === 'sendgrid_api'
        ),
      },
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    deliveryMethods() {
      return [
        {
          key: 1,
          value: 'smtp',
          label: this.$t('INBOX_MGMT.SMTP.DELIVERY_METHOD.SMTP'),
        },
        {
          key: 2,
          value: 'sendgrid_api',
          label: this.$t('INBOX_MGMT.SMTP.DELIVERY_METHOD.SENDGRID'),
        },
      ];
    },
    isSendgridDeliveryMethod() {
      return this.selectedDeliveryMethod === 'sendgrid_api';
    },
    isSmtpDeliveryEnabled() {
      return this.selectedDeliveryMethod === 'smtp' && this.isSMTPEnabled;
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const {
        outbound_delivery_method,
        smtp_enabled,
        smtp_address,
        smtp_port,
        smtp_login,
        smtp_password,
        smtp_domain,
        smtp_enable_starttls_auto,
        smtp_enable_ssl_tls,
        smtp_openssl_verify_mode,
        smtp_authentication,
        sendgrid_api_key,
        sendgrid_from_email,
        sendgrid_from_name,
      } = this.inbox;
      this.selectedDeliveryMethod = outbound_delivery_method || 'smtp';
      this.isSMTPEnabled = smtp_enabled;
      this.address = smtp_address;
      this.port = smtp_port;
      this.login = smtp_login;
      this.password = smtp_password;
      this.domain = smtp_domain;
      this.sendgridApiKey = sendgrid_api_key;
      this.sendgridFromEmail = sendgrid_from_email;
      this.sendgridFromName = sendgrid_from_name;
      this.starttls = smtp_enable_starttls_auto;
      this.ssl = smtp_enable_ssl_tls;
      this.openSSLVerifyMode = smtp_openssl_verify_mode;
      this.authMechanism = smtp_authentication;

      this.encryptionProtocols = [
        { id: 'ssl', title: 'SSL/TLS', checked: smtp_enable_ssl_tls },
        {
          id: 'starttls',
          title: 'STARTTLS',
          checked: smtp_enable_starttls_auto,
        },
      ];
    },
    handleEncryptionChange(encryption) {
      if (encryption.id === 'ssl') {
        this.ssl = true;
        this.starttls = false;
      } else {
        this.ssl = false;
        this.starttls = true;
      }
    },
    handleSSLModeChange(mode) {
      this.openSSLVerifyMode = mode;
    },
    handleAuthMechanismChange(mode) {
      this.authMechanism = mode;
    },
    handleDeliveryMethodChange(method) {
      this.selectedDeliveryMethod = method;
      if (this.selectedDeliveryMethod === 'sendgrid_api') {
        this.isSMTPEnabled = false;
      }
    },
    async updateInbox() {
      try {
        this.v$.$touch();
        if (this.v$.$invalid) {
          return;
        }

        const payload = {
          id: this.inbox.id,
          channel: {
            outbound_delivery_method: this.selectedDeliveryMethod,
            smtp_enabled: this.isSMTPEnabled,
            smtp_address: this.address,
            smtp_port: this.port,
            smtp_login: this.login,
            smtp_password: this.password,
            smtp_domain: this.domain,
            smtp_enable_ssl_tls: this.ssl,
            smtp_enable_starttls_auto: this.starttls,
            smtp_openssl_verify_mode: this.openSSLVerifyMode,
            smtp_authentication: this.authMechanism,
            sendgrid_api_key: this.sendgridApiKey,
            sendgrid_from_email: this.sendgridFromEmail,
            sendgrid_from_name: this.sendgridFromName,
          },
        };
        await this.$store.dispatch('inboxes/updateInboxSMTP', payload);
        useAlert(this.$t('INBOX_MGMT.SMTP.EDIT.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.SMTP.EDIT.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.SMTP.TITLE')"
      :sub-title="$t('INBOX_MGMT.SMTP.SUBTITLE')"
    >
      <form @submit.prevent="updateInbox">
        <SingleSelectDropdown
          class="max-w-[75%] w-full"
          :label="$t('INBOX_MGMT.SMTP.DELIVERY_METHOD.LABEL')"
          :selected="selectedDeliveryMethod"
          :options="deliveryMethods"
          :action="handleDeliveryMethodChange"
        />
        <template v-if="selectedDeliveryMethod === 'smtp'">
          <label for="toggle-enable-smtp">
            <input
              v-model="isSMTPEnabled"
              type="checkbox"
              name="toggle-enable-smtp"
            />
            {{ $t('INBOX_MGMT.SMTP.TOGGLE_AVAILABILITY') }}
          </label>
          <p>{{ $t('INBOX_MGMT.SMTP.TOGGLE_HELP') }}</p>
        </template>
        <div
          v-if="selectedDeliveryMethod === 'smtp' && isSMTPEnabled"
          class="mb-6"
        >
          <woot-input
            v-model="address"
            :class="{ error: v$.address.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.ADDRESS.LABEL')"
            :placeholder="$t('INBOX_MGMT.SMTP.ADDRESS.PLACE_HOLDER')"
            @blur="v$.address.$touch"
          />
          <woot-input
            v-model="port"
            type="number"
            :class="{ error: v$.port.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.PORT.LABEL')"
            :placeholder="$t('INBOX_MGMT.SMTP.PORT.PLACE_HOLDER')"
            @blur="v$.port.$touch"
          />
          <woot-input
            v-model="login"
            :class="{ error: v$.login.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.LOGIN.LABEL')"
            :placeholder="$t('INBOX_MGMT.SMTP.LOGIN.PLACE_HOLDER')"
            @blur="v$.login.$touch"
          />
          <woot-input
            v-model="password"
            :class="{ error: v$.password.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.PASSWORD.LABEL')"
            :placeholder="$t('INBOX_MGMT.SMTP.PASSWORD.PLACE_HOLDER')"
            type="password"
            @blur="v$.password.$touch"
          />
          <woot-input
            v-model="domain"
            :class="{ error: v$.domain.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.DOMAIN.LABEL')"
            :placeholder="$t('INBOX_MGMT.SMTP.DOMAIN.PLACE_HOLDER')"
            @blur="v$.domain.$touch"
          />
          <InputRadioGroup
            :label="$t('INBOX_MGMT.SMTP.ENCRYPTION')"
            :items="encryptionProtocols"
            :action="handleEncryptionChange"
          />
          <SingleSelectDropdown
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.OPEN_SSL_VERIFY_MODE')"
            :selected="openSSLVerifyMode"
            :options="openSSLVerifyModes"
            :action="handleSSLModeChange"
          />
          <SingleSelectDropdown
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.AUTH_MECHANISM')"
            :selected="authMechanism"
            :options="authMechanisms"
            :action="handleAuthMechanismChange"
          />
        </div>
        <div v-if="isSendgridDeliveryMethod" class="mb-6">
          <woot-input
            v-model="sendgridApiKey"
            :class="{ error: v$.sendgridApiKey.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.SENDGRID.API_KEY.LABEL')"
            :placeholder="$t('INBOX_MGMT.SMTP.SENDGRID.API_KEY.PLACE_HOLDER')"
            type="password"
            @blur="v$.sendgridApiKey.$touch"
          />
          <woot-input
            v-model="sendgridFromEmail"
            :class="{ error: v$.sendgridFromEmail.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.SENDGRID.FROM_EMAIL.LABEL')"
            :placeholder="
              $t('INBOX_MGMT.SMTP.SENDGRID.FROM_EMAIL.PLACE_HOLDER')
            "
            @blur="v$.sendgridFromEmail.$touch"
          />
          <woot-input
            v-model="sendgridFromName"
            :class="{ error: v$.sendgridFromName.$error }"
            class="max-w-[75%] w-full"
            :label="$t('INBOX_MGMT.SMTP.SENDGRID.FROM_NAME.LABEL')"
            :placeholder="$t('INBOX_MGMT.SMTP.SENDGRID.FROM_NAME.PLACE_HOLDER')"
            @blur="v$.sendgridFromName.$touch"
          />
        </div>
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.SMTP.UPDATE')"
          :is-loading="uiFlags.isUpdatingSMTP"
          :disabled="v$.$invalid || uiFlags.isUpdatingSMTP"
        />
      </form>
    </SettingsSection>
  </div>
</template>
